import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, TextField, FormControlLabel, Switch, Button,
  Alert, Snackbar, CircularProgress, Paper, Typography, IconButton,
  InputAdornment, Stack, Chip, Skeleton,
} from '@mui/material';
import {
  Save as SaveIcon, ContentCopy as CopyIcon, CheckCircle as CheckIcon,
  Visibility, VisibilityOff, Close as CloseIcon,
  VpnKey as KeyIcon, Webhook as WebhookIcon, Tune as TuneIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { centralSchoolPayAPI, BASE_URL } from '../../services/api';

const emptySettings = {
  schoolpay_code: '',
  sync_password: '',
  adhoc_password: '',
  webhook_secret: '',
  webhook_enabled: false,
  sync_enabled: false,
  auto_match_payments: true,
  callback_url: '',
};

const SectionCard = ({ icon, title, description, children }) => (
  <Box sx={{ mb: 3 }}>
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1.5,
        bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 16, color: 'white' } })}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary">{description}</Typography>
        )}
      </Box>
    </Stack>
    <Box sx={{ pl: 0 }}>
      {children}
    </Box>
  </Box>
);

const TenantSchoolPaySettingsDrawer = ({ open, onClose, tenant, getConfig }) => {
  const [settings, setSettings] = useState(emptySettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [showPasswords, setShowPasswords] = useState({
    sync_password: false,
    adhoc_password: false,
    webhook_secret: false,
  });

  const getWebhookUrl = () => `${BASE_URL}/api/webhooks/schoolpay?tenant=${tenant?.tenant_code || ''}`;
  const getAdhocCallbackUrl = () => `${BASE_URL}/api/webhooks/schoolpay/adhoc-callback?tenant=${tenant?.tenant_code || ''}`;

  const fetchSettings = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const { data } = await centralSchoolPayAPI.getSettings(tenant.id, getConfig());
      setConfigured(data.configured);
      setSettings({
        ...emptySettings,
        schoolpay_code: data.schoolpay_code || '',
        webhook_enabled: data.webhook_enabled || false,
        sync_enabled: data.sync_enabled || false,
        auto_match_payments: data.auto_match_payments ?? true,
        callback_url: data.callback_url || getAdhocCallbackUrl(),
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load SchoolPay settings.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && tenant) {
      fetchSettings();
    } else {
      setSettings(emptySettings);
      setConfigured(false);
      setMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenant]);

  const handleSubmit = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = { ...settings };
      if (!payload.sync_password) delete payload.sync_password;
      if (!payload.adhoc_password) delete payload.adhoc_password;
      if (!payload.webhook_secret) delete payload.webhook_secret;

      await centralSchoolPayAPI.saveSettings(tenant.id, payload, getConfig());
      setMessage({ type: 'success', text: 'SchoolPay settings saved successfully.' });
      setConfigured(true);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (url, field) => {
    navigator.clipboard.writeText(url);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const togglePassword = (field) =>
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));

  const set = (field) => (e) =>
    setSettings(prev => ({ ...prev, [field]: e.target.value }));

  const setToggle = (field) => (e) =>
    setSettings(prev => ({ ...prev, [field]: e.target.checked }));

  const PasswordEndAdornment = ({ field }) => (
    <InputAdornment position="end">
      <IconButton size="small" onClick={() => togglePassword(field)} edge="end">
        {showPasswords[field] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  const UrlRow = ({ label, url, field }) => (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all', color: 'text.primary' }}>
          {url}
        </Typography>
        <IconButton size="small" onClick={() => handleCopy(url, field)} sx={{ flexShrink: 0 }}>
          {copiedField === field
            ? <CheckIcon fontSize="small" color="success" />
            : <CopyIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Paper>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 640 }, display: 'flex', flexDirection: 'column' } } }}
    >
      {/* Header */}
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>SchoolPay Settings</Typography>
            {configured && <Chip label="Configured" color="success" size="small" />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {tenant?.data?.school_name || tenant?.tenant_code || ''}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
      </Stack>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
        {loading ? (
          <Box sx={{ pt: 1 }}>
            {/* Section 1 skeleton */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Skeleton variant="rounded" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={220} height={16} />
              </Box>
            </Stack>
            <Skeleton variant="rounded" height={40} sx={{ mb: 3 }} />

            {/* Section 2 skeleton */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Skeleton variant="rounded" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={260} height={16} />
              </Box>
            </Stack>
            <Skeleton variant="rounded" height={56} sx={{ mb: 1.5 }} />
            <Skeleton variant="rounded" height={56} sx={{ mb: 1.5 }} />
            <Skeleton variant="rounded" height={40} sx={{ mb: 3 }} />

            {/* Section 3 skeleton */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Skeleton variant="rounded" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={240} height={16} />
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
              <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
            </Stack>
            <Skeleton variant="rounded" height={40} sx={{ mb: 3 }} />

            {/* Section 4 skeleton */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Skeleton variant="rounded" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="text" width={200} height={16} />
              </Box>
            </Stack>
            {[...Array(3)].map((_, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5, borderBottom: i < 2 ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Box>
                  <Skeleton variant="text" width={160} height={18} />
                  <Skeleton variant="text" width={220} height={14} />
                </Box>
                <Skeleton variant="rounded" width={34} height={20} />
              </Stack>
            ))}
          </Box>
        ) : (
          <>
            {/* Section 1 — Identity */}
            <SectionCard
              icon={<SchoolIcon />}
              title="School Identity"
              description="Core SchoolPay account identifier for this school"
            >
              <TextField
                fullWidth size="small"
                label="SchoolPay School Code"
                required
                value={settings.schoolpay_code}
                onChange={set('schoolpay_code')}
                placeholder="e.g. SCH001"
                helperText="Provided by SchoolPay — used for all API calls"
              />
            </SectionCard>

            {/* Section 2 — Webhook URLs */}
            <SectionCard
              icon={<WebhookIcon />}
              title="Webhook URLs"
              description="Copy these into the SchoolPay portal to receive payment notifications"
            >
              <UrlRow label="Payment Webhook URL" url={getWebhookUrl()} field="webhook" />
              <UrlRow label="Adhoc Payment Callback URL" url={getAdhocCallbackUrl()} field="adhoc_callback" />
              <TextField
                fullWidth size="small"
                label="Custom Adhoc Callback URL"
                type="url"
                value={settings.callback_url}
                onChange={set('callback_url')}
                placeholder={getAdhocCallbackUrl()}
                helperText="Override the default callback URL if needed — must be different from the Webhook URL"
                sx={{ mt: 1 }}
              />
            </SectionCard>

            {/* Section 3 — Passwords */}
            <SectionCard
              icon={<KeyIcon />}
              title="API Credentials"
              description={configured ? 'Passwords are stored — leave blank to keep existing values' : 'Set passwords to enable SchoolPay API access'}
            >
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth size="small"
                    label="Transaction Sync Password"
                    type={showPasswords.sync_password ? 'text' : 'password'}
                    value={settings.sync_password}
                    onChange={set('sync_password')}
                    placeholder={configured ? '••••••••' : 'Enter password'}
                    helperText="Used when pulling transactions from SchoolPay"
                    InputProps={{ endAdornment: <PasswordEndAdornment field="sync_password" /> }}
                  />
                  <TextField
                    fullWidth size="small"
                    label="Adhoc Payments Password"
                    type={showPasswords.adhoc_password ? 'text' : 'password'}
                    value={settings.adhoc_password}
                    onChange={set('adhoc_password')}
                    placeholder={configured ? '••••••••' : 'Enter password'}
                    helperText="Used when initiating adhoc payment requests"
                    InputProps={{ endAdornment: <PasswordEndAdornment field="adhoc_password" /> }}
                  />
                </Stack>
                <TextField
                  fullWidth size="small"
                  label="Webhook Verification Secret"
                  type={showPasswords.webhook_secret ? 'text' : 'password'}
                  value={settings.webhook_secret}
                  onChange={set('webhook_secret')}
                  placeholder={configured ? '••••••••' : 'Enter secret'}
                  helperText="Used to verify SHA256 HMAC signatures on incoming webhook payloads"
                  InputProps={{ endAdornment: <PasswordEndAdornment field="webhook_secret" /> }}
                />
              </Stack>
            </SectionCard>

            {/* Section 4 — Feature Toggles */}
            <SectionCard
              icon={<TuneIcon />}
              title="Features"
              description="Control which SchoolPay integrations are active for this school"
            >
              <Stack spacing={0}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Automatic Daily Sync</Typography>
                    <Typography variant="caption" color="text.secondary">Automatically pull transactions from SchoolPay every day</Typography>
                  </Box>
                  <Switch size="small" checked={settings.sync_enabled} onChange={setToggle('sync_enabled')} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Webhook Notifications</Typography>
                    <Typography variant="caption" color="text.secondary">Receive real-time payment events via webhook</Typography>
                  </Box>
                  <Switch size="small" checked={settings.webhook_enabled} onChange={setToggle('webhook_enabled')} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', py: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-Match Payments</Typography>
                    <Typography variant="caption" color="text.secondary">Automatically match incoming transactions to students</Typography>
                  </Box>
                  <Switch size="small" checked={settings.auto_match_payments} onChange={setToggle('auto_match_payments')} />
                </Box>
              </Stack>
            </SectionCard>
          </>
        )}
      </Box>

      {/* Footer */}
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || saving || !settings.schoolpay_code}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Stack>

      <Snackbar
        open={!!message}
        autoHideDuration={6000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setMessage(null)} severity={message?.type || 'info'} sx={{ width: '100%' }}>
          {message?.text}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default TenantSchoolPaySettingsDrawer;
